/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { gatewayListPolicies } from "../funcs/gatewayListPolicies.js";
import { gatewaySetPolicies } from "../funcs/gatewaySetPolicies.js";
import { gatewayUpdatePolicy } from "../funcs/gatewayUpdatePolicy.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import { unwrapAsync } from "../types/fp.js";

export class Gateway extends ClientSDK {
  /**
   * List policies
   *
   * @remarks
   * Retrieve an environment's gateway policies in evaluation order: the
   * gateway evaluates them top to bottom and the first rejection
   * short-circuits the request.
   *
   * The full policy list is returned in a single response.
   *
   * **Required Permissions**
   *
   * Your API key must have one of the following permissions:
   * - `environment.*.read_policies` (for any environment)
   * - `environment.<environment_id>.read_policies` (for a specific environment)
   */
  async listPolicies(
    request: components.V1GatewayListPoliciesRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1GatewayListPoliciesResponseBody> {
    return unwrapAsync(gatewayListPolicies(
      this,
      request,
      options,
    ));
  }

  /**
   * Set policies
   *
   * @remarks
   * Replace an environment's gateway policies in a single atomic request.
   * Policies run at the edge before requests reach your app: block requests
   * outright or log them.
   *
   * Policies are an ordered list: the gateway evaluates them top to bottom
   * and the first rejection short-circuits the request.
   *
   * Each policy sets exactly one of `firewall` or `logging`, plus optional `match` expressions restricting which requests
   * it applies to.
   *
   * Every call is a full replace: the environment's policies become exactly
   * the request list in the given order, and the server generates a fresh id
   * for each one. An empty list removes all policies. The operation is
   * atomic: if any policy is invalid, nothing is written.
   *
   * **Required Permissions**
   *
   * Your API key must have one of the following permissions:
   * - `environment.*.set_policies` (for any environment)
   * - `environment.<environment_id>.set_policies` (for a specific environment)
   */
  async setPolicies(
    request: components.V1GatewaySetPoliciesRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1GatewaySetPoliciesResponseBody> {
    return unwrapAsync(gatewaySetPolicies(
      this,
      request,
      options,
    ));
  }

  /**
   * Update policy
   *
   * @remarks
   * Update a single policy in place without resending the environment's full
   * policy list. The policy keeps its id and its position in the evaluation
   * order, and all other policies are untouched.
   *
   * Omitted fields keep their stored values; at least one updatable field
   * must be provided. Setting `match` to null removes all match expressions
   * so the policy applies to every request. Providing one of `firewall` or
   * `logging` replaces the policy's rule
   * entirely, including switching its type; at most one may be set.
   *
   * Policy ids are regenerated whenever `gateway.setPolicies` replaces the
   * list, so fetch current ids via `gateway.listPolicies` first.
   *
   * **Required Permissions**
   *
   * Your API key must have one of the following permissions:
   * - `environment.*.update_policy` (for any environment)
   * - `environment.<environment_id>.update_policy` (for a specific environment)
   */
  async updatePolicy(
    request: components.V1GatewayUpdatePolicyRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1GatewayUpdatePolicyResponseBody> {
    return unwrapAsync(gatewayUpdatePolicy(
      this,
      request,
      options,
    ));
  }
}
